import {
  weightedDailyLoad,
  weightedDailyProductivity,
  type LoadBlockInput,
  type ProductivityBlockInput,
} from "../../lib/effectiveLoad";
import type { SessionWithRatings } from "../../types/pomoprogress";
import type { ChartPoint, DummyBarDataset } from "./dummyData";
import { getProductivityBarColor } from "./dummyData";
import { getMonthDayMetas, getYearMonthMetas } from "./chartLabels";

const NO_DATA_BAR_COLOR = "rgb(90, 90, 90)";

function collectLoadBlocksFromSessions(sessions: SessionWithRatings[]): LoadBlockInput[] {
  const blocks: LoadBlockInput[] = [];
  for (const session of sessions) {
    for (const row of session.block_ratings ?? []) {
      blocks.push({
        load: row.load,
        workType: row.work_type,
        durationSeconds: row.duration_seconds,
      });
    }
  }
  return blocks;
}

function collectProductivityBlocksFromSessions(sessions: SessionWithRatings[]): ProductivityBlockInput[] {
  const blocks: ProductivityBlockInput[] = [];
  for (const session of sessions) {
    for (const row of session.block_ratings ?? []) {
      blocks.push({
        rating: row.rating,
        workType: row.work_type,
        durationSeconds: row.duration_seconds,
      });
    }
  }
  return blocks;
}

/** Day totals for Focus cards: work seconds, weighted productivity (1–10), effective load (1–5). */
export function summarizeDayFromSessions(sessions: SessionWithRatings[]): {
  totalSeconds: number;
  productivityAvg: number;
  ratingCount: number;
  loadAvg: number;
  loadCount: number;
} {
  const productivitySummary = weightedDailyProductivity(collectProductivityBlocksFromSessions(sessions));
  const loadSummary = weightedDailyLoad(collectLoadBlocksFromSessions(sessions));
  const totalSeconds = sessions.reduce((sum, session) => sum + session.total_time_worked, 0);
  return {
    totalSeconds,
    productivityAvg: productivitySummary.productivityAvg,
    ratingCount: productivitySummary.ratingCount,
    loadAvg: loadSummary.loadAvg,
    loadCount: loadSummary.loadCount,
  };
}

function groupSessionsByDate(sessions: SessionWithRatings[]): Map<string, SessionWithRatings[]> {
  const map = new Map<string, SessionWithRatings[]>();
  for (const session of sessions) {
    const existing = map.get(session.date) ?? [];
    existing.push(session);
    map.set(session.date, existing);
  }
  return map;
}

/** Per-calendar-day hours (sum of `total_time_worked` / 3600); updates after each rated block. */
export function buildMonthHoursLineSeriesFromSessions(
  sessions: SessionWithRatings[],
  year: number,
  monthIndex0: number
): { labels: string[]; hoursSeries: number[]; daysWorkedSeries: number[] } {
  const byDate = groupSessionsByDate(sessions);
  const dayMetas = getMonthDayMetas(year, monthIndex0, "full");
  const labels: string[] = [];
  const hoursSeries: number[] = [];
  const daysWorkedSeries: number[] = [];
  for (const { iso, label } of dayMetas) {
    labels.push(label);
    const daySessions = byDate.get(iso) ?? [];
    const totalSeconds = daySessions.reduce((sum, session) => sum + session.total_time_worked, 0);
    const dayHours = Number((totalSeconds / 3600).toFixed(3));
    hoursSeries.push(dayHours);
    daysWorkedSeries.push(dayHours > 0 ? 1 : 0);
  }
  return { labels, hoursSeries, daysWorkedSeries };
}

/** Per-calendar-month hours for a year; updates after each rated block. */
export function buildYearHoursLineSeriesFromSessions(
  sessions: SessionWithRatings[],
  year: number
): { labels: string[]; hoursSeries: number[]; daysWorkedSeries: number[] } {
  const monthMetas = getYearMonthMetas(year);
  const labels: string[] = [];
  const hoursSeries: number[] = [];
  const daysWorkedSeries: number[] = [];
  for (const { label, startDate, endDate } of monthMetas) {
    labels.push(label);
    const monthSessions = sessions.filter(
      (session) => session.date >= startDate && session.date <= endDate
    );
    const totalSeconds = monthSessions.reduce((sum, session) => sum + session.total_time_worked, 0);
    hoursSeries.push(Number((totalSeconds / 3600).toFixed(3)));
    const totalSecondsByDate = new Map<string, number>();
    for (const session of monthSessions) {
      const existingSeconds = totalSecondsByDate.get(session.date) ?? 0;
      totalSecondsByDate.set(session.date, existingSeconds + session.total_time_worked);
    }
    const daysOverOneHour = Array.from(totalSecondsByDate.values()).filter(
      (secondsWorked) => secondsWorked / 3600 >= 1
    ).length;
    daysWorkedSeries.push(daysOverOneHour);
  }
  return { labels, hoursSeries, daysWorkedSeries };
}

export function buildMonthBarDatasetFromSessions(
  sessions: SessionWithRatings[],
  year: number,
  monthIndex0: number
): DummyBarDataset {
  const byDate = groupSessionsByDate(sessions);
  const dayMetas = getMonthDayMetas(year, monthIndex0);

  const monthData: ChartPoint[] = dayMetas.map(({ iso, label }) => {
    const daySessions = byDate.get(iso) ?? [];
    const productivity = weightedDailyProductivity(collectProductivityBlocksFromSessions(daySessions));
    const productivityAvg = productivity.productivityAvg;
    const totalSeconds = daySessions.reduce((sum, session) => sum + session.total_time_worked, 0);
    const lengthHours = Number((totalSeconds / 3600).toFixed(1));
    const sessionCount = daySessions.filter((session) => session.sessions_completed === 1).length;
    const blocksCompleted = daySessions.reduce((sum, session) => sum + session.blocks_completed, 0);
    const backgroundColor =
      productivity.ratingCount === 0 ? NO_DATA_BAR_COLOR : getProductivityBarColor(productivityAvg);

    return {
      x: label,
      y: productivityAvg,
      length: lengthHours,
      sessions: sessionCount,
      backgroundColor,
      blocksCompleted,
    };
  });

  return {
    label: "Productivity avg",
    data: monthData,
    backgroundColor: monthData.map((point) => point.backgroundColor),
    borderColor: "black",
    borderWidth: 3.5,
  };
}

export function buildYearBarDatasetFromSessions(sessions: SessionWithRatings[], year: number): DummyBarDataset {
  const monthMetas = getYearMonthMetas(year);

  const yearData: ChartPoint[] = monthMetas.map(({ label, startDate, endDate }) => {
    const monthSessions = sessions.filter(
      (session) => session.date >= startDate && session.date <= endDate
    );
    const productivity = weightedDailyProductivity(collectProductivityBlocksFromSessions(monthSessions));
    const productivityAvg = productivity.productivityAvg;
    const totalSeconds = monthSessions.reduce((sum, session) => sum + session.total_time_worked, 0);
    const lengthHours = Number((totalSeconds / 3600).toFixed(1));
    const sessionCount = monthSessions.filter((session) => session.sessions_completed === 1).length;
    const blocksCompleted = monthSessions.reduce((sum, session) => sum + session.blocks_completed, 0);
    const backgroundColor =
      productivity.ratingCount === 0 ? NO_DATA_BAR_COLOR : getProductivityBarColor(productivityAvg);

    return {
      x: label,
      y: productivityAvg,
      length: lengthHours,
      sessions: sessionCount,
      backgroundColor,
      blocksCompleted,
    };
  });

  return {
    label: "Productivity avg",
    data: yearData,
    backgroundColor: yearData.map((point) => point.backgroundColor),
    borderColor: "black",
    borderWidth: 3.5,
  };
}
